import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { FormikLabeledTextField } from "../shared/form/FormikLabeledTextField";
import FormikSelect from "../shared/form/FormikSelect";
import { XL_GRID_PROPS, M_GRID_PROPS } from "../../constants";
import CustomDialog from "../confirmationPopup/confirmationPopup";
import { checkPro } from "../../API/checkPro";

export interface IClearExceptionFormValues {
  piecesOpen: string;
  piecesCleared: string;
  freightBillPro: string;
  code: string;
  comments: string;
}

interface ClearExceptionModalProps {
  isOpen: boolean;
  osdNumber: string | number | undefined;
  piecesOpen: number;
  onClose: () => void;
  onConfirm: (values: IClearExceptionFormValues) => void;
  isLoading?: boolean;
}

const CLEARING_CODE_OPTIONS = [
  { value: "CP", label: "CP - Claim Paid" },
  { value: "DA", label: "DA - Cleared on Free Astray" },
  { value: "CG", label: "CG - Cleared to Cargo" },
  { value: "SV", label: "SV - Cleared to Salvage" },
  { value: "CA", label: "CA - Consignee Agreed to Accept Freight" },
  { value: "RC", label: "RC - Reconsigned" },
  { value: "RI", label: "RI - Returned to Interline" },
  { value: "RS", label: "RS - Returned to Shipper" },
  { value: "CLP", label: "CLP - Claim Prevention" },
];

// Validation schema
const createValidationSchema = (piecesOpen: number, validateProNumber: (value: string) => Promise<boolean>) =>
  Yup.object().shape({
    piecesCleared: Yup.string()
      .required("Pieces Cleared is required")
      .matches(/^\d+$/, "Pieces Cleared must be numeric")
      .test("not-zero", "Pieces Cleared cannot be 0", (value) => {
        return value !== "0" && parseInt(value || "0", 10) !== 0;
      })
      .test(
        "not-exceed-open",
        "Pieces Cleared cannot exceed Pieces Open",
        (value) => {
          const cleared = parseInt(value || "0", 10);
          return cleared <= piecesOpen;
        }
      ),
    code: Yup.string().required("Clearing Code is required"),
    freightBillPro: Yup.string()
      .test(
        "valid-pro-format",
        "Freight Bill/PRO # must be exactly 11 digits",
        (value) => !value || /^\d{11}$/.test(value)
      )
      .test(
        "valid-pro-exists",
        "Invalid Freight Bill/PRO #",
        async function(value) {
          if (!value || !/^\d{11}$/.test(value)) {
            return true; // Skip API validation if format is invalid
          }
          return await validateProNumber(value);
        }
      ),
    comments: Yup.string()
      .max(35, "Comments cannot exceed 35 characters")
      .matches(
        /^[a-zA-Z0-9\s.,!?'-]*$/,
        "Comments must be alphanumeric"
      ),
  });

const ClearExceptionModal: React.FC<ClearExceptionModalProps> = ({
  isOpen,
  osdNumber,
  piecesOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [validatedProNumbers, setValidatedProNumbers] = useState<Map<string, boolean>>(new Map());
  const [showPartialClearConfirm, setShowPartialClearConfirm] = useState(false);
  const inFlightRequests = React.useRef<Map<string, Promise<boolean>>>(new Map());

  // Function to validate PRO number using the API
  const validateProNumber = async (proNumber: string): Promise<boolean> => {
    // Check cache first
    if (validatedProNumbers.has(proNumber)) {
      return validatedProNumbers.get(proNumber)!;
    }

    // Check if there's already an in-flight request for this PRO
    if (inFlightRequests.current.has(proNumber)) {
      return inFlightRequests.current.get(proNumber)!;
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        const response = await checkPro(proNumber);
        // Check if response is empty object or has no data
        const isValid = response && Object.keys(response).length > 0;
        
        // Cache the result
        setValidatedProNumbers(prev => new Map(prev).set(proNumber, isValid));
        
        return isValid;
      } catch (error) {
        console.error("Error validating PRO number:", error);
        // Cache as invalid on error
        setValidatedProNumbers(prev => new Map(prev).set(proNumber, false));
        return false;
      } finally {
        // Remove from in-flight requests
        inFlightRequests.current.delete(proNumber);
      }
    })();

    // Store the promise
    inFlightRequests.current.set(proNumber, requestPromise);

    return requestPromise;
  };

  const validationSchema = createValidationSchema(piecesOpen, validateProNumber);

  const formik = useFormik<IClearExceptionFormValues>({
    initialValues: {
      piecesOpen: piecesOpen?.toString() || "0",
      piecesCleared: "",
      freightBillPro: "",
      code: "",
      comments: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      onConfirm(values);
    },
  });

  // Reset form when modal opens/closes or piecesOpen changes
  useEffect(() => {
    if (isOpen) {
      formik.resetForm({
        values: {
          piecesOpen: piecesOpen?.toString() || "0",
          piecesCleared: "",
          freightBillPro: "",
          code: "",
          comments: "",
        },
      });
      // Clear validated PRO numbers cache and in-flight requests
      setValidatedProNumbers(new Map());
      inFlightRequests.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, piecesOpen]);

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleReset = () => {
    formik.resetForm();
  };

  const handleConfirmClick = async () => {
    // Touch all fields to show validation errors
    await formik.setTouched({
      piecesCleared: true,
      code: true,
      freightBillPro: true,
      comments: true,
    });
    
    // Validate form (including async PRO validation)
    const errors = await formik.validateForm();
    
    // Only proceed if no errors
    if (Object.keys(errors).length === 0) {
      // Check if pieces don't match
      const piecesCleared = parseInt(formik.values.piecesCleared, 10);
      if (piecesCleared !== piecesOpen) {
        // Show confirmation popup for partial clear
        setShowPartialClearConfirm(true);
      } else {
        formik.handleSubmit();
      }
    }
  };

  const handleProBlur = async () => {
    const proValue = formik.values.freightBillPro;
    if (proValue && /^\d{11}$/.test(proValue)) {
      // Trigger validation for the PRO field
      await formik.validateField('freightBillPro');
    }
    formik.handleBlur('freightBillPro');
  };

  const handlePartialClearConfirm = (flag?: string) => {
    setShowPartialClearConfirm(false);
    if (flag === "yes") {
      formik.handleSubmit();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      key="clear-switch-modal"
      aria-labelledby="clear-dialog-title"
      keepMounted
      maxWidth="md"
      fullWidth={true}
      className="assignment"
      data-testid="clear-switch-dialog"
    >
      <DialogTitle
        sx={{ m: 0, p: 2 }}
        id="clear-dialog-title"
        className="modal-header"
        data-testid="clear-dialog-title"
      >
        <Box display="flex" alignItems="center" gap={1}>
          Exception<span className="exceptionNo"> #{osdNumber}</span>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleCancel}
        disabled={isLoading}
        sx={{
          position: "absolute",
          right: 15,
          top: 15,
          color: (theme: any) => theme.palette.grey[500],
        }}
        data-testid="clear-close-button"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers data-testid="clear-dialog-content">
        <Box className="misdelivery-ivestigation-container">
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Grid
                container
                spacing={2}
                className="filter-margin"
                id="investigation-form"
              >
                <FormikLabeledTextField
                  formik={formik}
                  name="piecesOpen"
                  label="Pieces Open"
                  placeholder="Pieces open"
                  gridProps={M_GRID_PROPS}
                  disabled={true}
                />
                <FormikLabeledTextField
                  formik={formik}
                  name="piecesCleared"
                  label="Pieces Cleared"
                  placeholder="Enter pieces cleared"
                  gridProps={M_GRID_PROPS}
                  type="number"
                  maxLength={5}
                  required={true}
                />
                <FormikSelect
                  name="code"
                  label="Clearing Code"
                  value={formik.values.code ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  options={CLEARING_CODE_OPTIONS}
                  error={
                    formik.touched.code && typeof formik.errors.code === "string"
                      ? formik.errors.code
                      : undefined
                  }
                  gridProps={M_GRID_PROPS}
                  required={true}
                />
                <FormikLabeledTextField
                  formik={formik}
                  name="freightBillPro"
                  label="Freight Bill/PRO #"
                  placeholder="Enter 11-digit PRO #"
                  gridProps={M_GRID_PROPS}
                  type="number"
                  maxLength={11}
                  onBlur={handleProBlur}
                />
                <FormikLabeledTextField
                  formik={formik}
                  name="comments"
                  label="Comments"
                  placeholder="Enter comments"
                  gridProps={XL_GRID_PROPS}
                  maxLength={35}
                />
              </Grid>
            </form>
          </FormikProvider>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleReset}
          variant="contained"
          className="clear-btn"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmClick}
          variant="contained"
          className="entry ml"
          disabled={isLoading}
        >
          {isLoading ? "Clearing..." : "Clear"}
        </Button>
      </DialogActions>
      <CustomDialog
        open={showPartialClearConfirm}
        onClose={handlePartialClearConfirm}
        message={`Pieces Cleared (${formik.values.piecesCleared}) doesn't match Pieces Open (${piecesOpen}).<br /><br />Do you want to proceed with partial clear?`}
        isConfirmation={true}
      />
    </Dialog>
  );
};

export default ClearExceptionModal;
